import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SidePane } from '@/components/data';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

/**
 * Recording a movement from a phone.
 *
 * On a desktop the adjust form is a strip that opens under the row, which works
 * because the row is on screen beside it. On a phone that strip lives inside a
 * table far wider than the viewport, so the fields are somewhere off to the
 * right of the item they belong to — unusable, which is why the store keeper
 * still walked back to a desk to key the day in.
 *
 * Here it is the whole screen instead: direction as two big buttons, one large
 * numeric field, and the resulting stock spelled out before Record is pressed.
 * The server refuses a stock out that would go below zero, so this says so first
 * rather than letting the keeper find out from a toast.
 */

const num = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function MovementSheet({ item, form, onChange, submitting, onSubmit, onClose }) {
  const out = form.type === 'stock_out';
  const qty = parseFloat(form.quantity) || 0;
  const after = out ? item.current_stock - qty : item.current_stock + qty;
  const wouldGoNegative = out && after < 0;
  const wouldGoLow = !wouldGoNegative && item.minimum_stock > 0 && after <= item.minimum_stock;

  const set = (key) => (e) => onChange({ ...form, [key]: e.target.value });

  return (
    <SidePane
      open
      overlay
      onClose={onClose}
      title={item.name}
      subtitle={`${num(item.current_stock)} ${item.unit} in stock`}
      width="26rem"
      data-testid="movement-sheet"
      footer={
        <>
          <Button variant="outline" className="h-12 px-4 text-sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className={cn(
              'h-12 flex-1 text-base font-semibold text-white',
              out ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
            )}
            disabled={submitting || qty <= 0 || wouldGoNegative}
            onClick={onSubmit}
            data-testid="record-movement"
          >
            {submitting ? 'Saving…' : out ? 'Record stock out' : 'Record stock in'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 p-3">
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Direction">
          {[
            ['stock_in', 'Stock In', ArrowUpCircle, 'bg-emerald-600', 'text-emerald-700'],
            ['stock_out', 'Stock Out', ArrowDownCircle, 'bg-red-600', 'text-red-600'],
          ].map(([value, label, Icon, activeBg, idleText]) => {
            const active = form.type === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                data-testid={`direction-${value}`}
                onClick={() => onChange({ ...form, type: value })}
                className={cn(
                  'flex h-14 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-colors',
                  active
                    ? `${activeBg} border-transparent text-white shadow-sm`
                    : `border-slate-200 bg-white ${idleText}`
                )}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            );
          })}
        </div>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Quantity</span>
          <span className="mt-1 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 focus-within:border-brand-blue focus-within:ring-1 focus-within:ring-brand-blue">
            <Input
              type="number"
              inputMode="decimal"
              autoFocus
              placeholder="0"
              value={form.quantity}
              onChange={set('quantity')}
              data-testid="movement-quantity"
              className="h-14 border-0 px-0 text-right text-2xl font-bold tabular-nums shadow-none focus-visible:ring-0"
            />
            <span className="shrink-0 text-sm font-medium text-slate-400">{item.unit}</span>
          </span>
        </label>

        {qty > 0 && (
          <p
            data-testid="movement-preview"
            className={cn(
              'rounded-lg px-3 py-2.5 text-sm font-medium',
              wouldGoNegative ? 'bg-red-50 text-red-700'
                : wouldGoLow ? 'bg-amber-50 text-amber-800'
                  : 'bg-slate-50 text-slate-600'
            )}
          >
            <span className="tabular-nums">
              {num(item.current_stock)} → {num(after)} {item.unit}
            </span>
            {wouldGoNegative
              ? ' — the store only has this much; a stock out cannot take it below zero.'
              : wouldGoLow
                ? ` — at or below the ${num(item.minimum_stock)} minimum.`
                : ''}
          </p>
        )}

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Reference</span>
          <Input
            className="mt-1 h-12 text-base"
            placeholder="Bill / order number"
            value={form.reference}
            onChange={set('reference')}
            data-testid="movement-reference"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Remarks</span>
          <Textarea
            className="mt-1 min-h-[4.5rem] text-base"
            placeholder="Who took it, which job, anything worth remembering"
            value={form.remarks}
            onChange={set('remarks')}
            data-testid="movement-remarks"
          />
        </label>
      </div>
    </SidePane>
  );
}
